<template>
  <div class="bill-form px-3">
    <form @submit.stop.prevent="form.handleSubmit()">
      <!-- upload attachment btn & modal -->
      <div>
        <BaseButton
          variant="secondary"
          text="Upload bill document (optional)"
          type="button"
          icon-class="i-mdi-paperclip"
          @click="open = true"
        />

        <Modal
          v-model="open"
          title="Upload bill document"
          width="w-full md:w-1/2"
          close-button-text="Close"
          :no-escape="true"
          :no-backdrop="true"
          :no-close-button="true"
          @close="setBillAttachments"
        >
          <template #body>
            <div class="mt-4">
              <AppFileUpload
                :storage-path="`bills/documents/attachments/`"
                @on-file-upload-completed="fileUploadCompleted"
              />
            </div>
          </template>
        </Modal>
      </div>

      <!-- attachments -->
      <div class="flex flex-wrap gap-4 mt-4">
        <div
          v-for="(file, index) in bill.attachments"
          :key="file"
          class="relative w-[90px] h-[90px] rounded-lg border border-slate-200 flex items-center justify-center mt-2 bg-slate-100"
        >
          <img
            v-if="isImage(file)"
            :src="file"
            class="object-cover rounded w-[90px] h-[90px] cursor-pointer"
            @click="expandImage(file)"
          >
          <div
            v-else
            class="flex items-center justify-center w-full h-full text-center bg-slate-200 rounded-lg cursor-pointer"
            @click="expandImage(file)"
          >
            <div class="text-dark font-weight-bold">
              {{ fileTypeToString(file) }}
            </div>
          </div>
          <span
            class="absolute top-0 right-0 p-1 flex justify-center items-center rounded-full border-transparent bg-black/50 hover:bg-black/75 transition duration-200 ease-in-out cursor-pointer overflow-hidden select-none size-[1.3rem]"
            @click="deleteAttachment(index)"
          >
            <span class="i-mdi-close text-white"></span>
          </span>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-8 mt-4">
        <div>
          <BaseInput
            v-if="billType === 'purchase'"
            :model-value="`${purchaseOrder.purchaseOrderId} - ${project.name}`"
            name="purchaseOrderId"
            label="Purchase Order"
            :disabled="true"
          />

          <BaseInput
            v-else
            :model-value="`${workOrder.workOrderId} - ${project.name}`"
            name="workOrderId"
            label="Work Order"
            :disabled="true"
          />
        </div>

        <div>
          <BaseInput
            :model-value="bill.billNumber"
            name="billNumber"
            label="BILL#"
            :disabled="true"
          />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-8">
        <div>
          <form.Field
            name="billDate"
            :validators="{
              onBlur: yup.string().required('Invoice Date is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputDate
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Invoice Date"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>

        <div>
          <form.Field
            name="dueDate"
            :validators="{
              onBlur: yup.string().required('Due Date is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputDate
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Due date"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>
      </div>

      <h4 class="mt-6">Items</h4>
      <hr class="mt-2">
      <!-- items -->
      <div>
        <form.Field
          name="items"
          :validators="{
            onChange: yup.array().min(1, 'At least one item is required')
          }"
        >
          <template #default="{ field }">
            <div
              v-if="(field.state.meta.errors && field.state.meta.errors[0]) && form.getFieldValue('items').length === 0"
              class="text-base/6 text-error sm:text-sm/6 mt-2 flex flex-col items-center"
            >
              <span class="i-heroicons-exclaimation-circle size-8" />
              <p class="text-base/6 text-error sm:text-sm/6">
                {{ field.state.meta.errors[0] }}
              </p>
            </div>

            <div
              v-for="(_, i) of field.state.value"
              :key="`item-${i}`"
            >
              <div class="flex justify-end mt-8">
                <span
                  class="i-heroicons-x-circle cursor-pointer hover:text-error h-[1.2rem] w-[1.2rem]"
                  @click="removeItem(field, Number(i))"
                />
              </div>

              <!-- Title, Quantity, Price -->
              <div class="flex justify-around gap-6">
                <div class="grow">
                  <form.Field
                    :key="`name-${i}`"
                    :name="( `items[${i}].name` as any)"
                    :validators="{
                      onBlur: yup.string().required('Title is required'),
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInput
                        v-model="(subField.state.value as string)"
                        type="text"
                        label="Title"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => subField.handleChange(e.target.value)"
                      />
                    </template>
                  </form.Field>
                </div>

                <div class="mx-2">
                  <form.Field
                    :key="`quantity-${i}`"
                    :name="(`items[${i}].quantity` as any)"
                    :validators="{
                      onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Quantity must be a positive number',(value) => !isNaN(Number(value)) && Number(value) > 0).required('Quantity is required')
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInput
                        v-model.number="(subField.state.value as string)"
                        step="any"
                        type="number"
                        :label="billType === 'purchase' ? 'Quantity' : 'Hours'"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => {
                          subField.handleChange(e.target.value);
                          setTotal()
                        }"
                      />
                    </template>
                  </form.Field>
                </div>

                <div class="grow">
                  <form.Field
                    :key="`unitPrice-${i}`"
                    :name="(`items[${i}].unitPrice` as any)"
                    :validators="{
                      onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Price must be a valid number',(value) => !isNaN(Number(value))).required('Price is required')
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInputCurrency
                        :model-value="(subField.state.value as string)"
                        label="Price"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => {
                          subField.handleChange(e.target.value);
                          setTotal()
                        }"
                      />
                    </template>
                  </form.Field>
                </div>

                <div class="mx-2 min-w-24">
                  <label
                    class="font-semibold"
                    for="total-price"
                  >Total</label>
                  <span
                    id="total-price"
                    class="block mt-3"
                  >{{ currency(toNumber((field.state.value[i] as any).quantity) * toNumber((field.state.value[i] as any).unitPrice)) }}</span>
                </div>
              </div>
            </div>

            <div class="my-5">
              <BaseButton
                variant="secondary"
                type="button"
                text="Add item"
                @click="addItem(field)"
              />
            </div>
          </template>
        </form.Field>
      </div>

      <div
        class="mt-5 w-fit ml-auto"
      >
        <div class="flex items-baseline justify-end bg-slate-100 px-4 pt-4 rounded-lg gap-4">
          <span class="text-slate-700">Total</span>
          <form.Field
            name="total"
            :validators="{
              onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Total must be a valid number',(value) => !isNaN(Number(value))).required('Total is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputCurrency
                :model-value="(field.state.value as string)"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>
      </div>

      <hr class="mt-6">

      <div class="flex justify-end gap-2 mt-3">
        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <BaseButton
              variant="base"
              text="Cancel"
              :to="{ name: 'ShowBill', params: { id: bill.id } }"
              :disabled="isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
            />

            <BaseButton
              variant="primary"
              text="Save"
              type="submit"
              :disabled="!canSubmit || isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
            />
          </template>
        </form.Subscribe>
      </div>
    </form>
  </div>
</template>
<script lang="ts" setup>
import { Bill, BillItem } from '@/models/bill'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import AppFileUpload from '@/components/app/AppFileUpload.vue'
import { currency, toNumber } from '@/utils'
import { PropType, ref } from 'vue'
import { WorkOrder } from '@/models/workOrder'
import { PurchaseOrder } from '@/models/purchaseOrder'
import StorageService from '@/services/StorageService'
import { useNotification } from '@kyvg/vue3-notification'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { decimalFormat } from '@/utils'
import BaseInputCurrency from '@/components/base/BaseInputCurrency.vue'
import Modal from '@/components/app/AppModal.vue'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import { Project } from '@/models/project'

const props = defineProps({
  model: {
    type: Object as PropType<Bill>,
    required: true
  },
  workOrder: {
    type: Object as PropType<WorkOrder>,
    default: new WorkOrder()
  },
  purchaseOrder: {
    type: Object as PropType<PurchaseOrder>,
    default: new PurchaseOrder()
  },
  project: {
    type: Object as PropType<Project>,
    default: new Project()
  },
  saveFunction: {
    type: Function,
    required: true
  }
})

const bill = ref(JSON.parse(JSON.stringify(props.model)))
bill.value.items = bill.value.items.map((item) => {
  if (item['cost'] && !item.unitPrice) {
    item.unitPrice = item['cost']
    delete item['cost']
  }
  return item
})


const billType = ref(bill.value.purchaseOrderId ? 'purchase' : 'workOrder')
const open = ref(false)
const tempAttachments = ref(bill.value.attachments)
const storageService = new StorageService()
const { notify } = useNotification()


const setBillAttachments = () => {
  open.value = false
  bill.value.attachments = tempAttachments.value
}

const fileUploadCompleted = (urls: string[]) => {
  tempAttachments.value = urls
}

const form = useForm({
  defaultValues: bill.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    const newBill = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as unknown as Bill
    newBill.items = newBill.items.map((item) => {
      const billItem = {
        ...item,
        quantity: toNumber(item.quantity),
        unitPrice: toNumber(item.unitPrice),
        total: item.quantity * toNumber(item.unitPrice)
      }
      return Object.fromEntries(Object.entries(billItem).filter(([, v]) => v !== undefined)) as unknown as BillItem
    })
    newBill.total = toNumber(newBill.total)
    newBill.balance = toNumber(newBill.total) - (props.model.total - props.model.balance)
    newBill.attachments = tempAttachments.value
    newBill.dueDate = new Date(newBill.dueDate)
    newBill.billDate = new Date(newBill.billDate)
    await props.saveFunction(newBill)
  }
})

const addItem = (field: any) => {
  field.pushValue(new BillItem())
}

const removeItem = (field: any, index: number) => {
  field.removeValue(index)
  setTotal()
}

const setTotal = () => {
  const total = form.getFieldValue('items').reduce((acc, item) => {
    return acc + (item.quantity * toNumber(item.unitPrice))
  }, 0)

  if (total) {
    form.setFieldValue('total', decimalFormat(total, 2) as any)
  } else {
    form.setFieldValue('total', String(0) as any)
  }
}

const isImage = (file: string) => {
  const ext = file.match(/\.([0-9a-z]+)(?:[\?#]|$)/i)
  return ext ? ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext[1].toLowerCase()) : false
}

const deleteAttachment = async (index: number) => {
  const attachment = bill.value.attachments[index]
  try {
    await storageService.deleteAttachmentWithStyledImages(attachment)
    bill.value.attachments.splice(index, 1)
    tempAttachments.value = tempAttachments.value.filter(att => att !== attachment)
  } catch (error) {
    console.log(error)
    notify({
      type: 'error',
      title: 'Error',
      text: 'Failed to delete attachment'
    })
  }
}

const fileTypeToString = (file: string) => {
  const ext = file.match(/\.([0-9a-z]+)(?:[\?#]|$)/i)
  return ext ? ext[1].toUpperCase() : 'FILE'
}

const expandImage = (imageURL: string) => {
  window.open(imageURL, '_blank')
}

</script>
